import type { ErrorEvent, EventHint } from "@sentry/react"

/**
 * Errors from code that isn't ours (passed to Sentry.init as ignoreErrors).
 * Matched against the error's "Type: message".
 */
export const IGNORE_ERRORS: (string | RegExp)[] = [
  // Instagram / Facebook in-app browser (Android): its injected script loses
  // its bridge to the app.
  "Java object is gone",
  // Instagram / Facebook in-app browser (iOS): its injected script calls the
  // app's bridge where there is none.
  "window.webkit.messageHandlers",
  // An Android browser's injected autofill script.
  "xbrowser is not defined",
  // Microsoft Outlook's link scanner (Safe Links) opening links from emails.
  "Object Not Found Matching Id",
  // supabase-js hands its auth lock to another tab or request, which then
  // refreshes the session itself.
  "Lock was stolen by another request",
  "Lock broken by another request with the 'steal' option",
  // Instagram / Facebook in-app browser (iOS): its injected script reads its
  // own `window._META` before it exists (PRODUCTION-TQ).
  "window._META.IABInnerFrame",
  // Chrome for iOS and the Google iOS app: their injected scripts throw
  // minified two-letter errors ("Error: La", "Error: pa", "Error: Ma") from
  // lines of the page our 52-line index.html doesn't have. Exact matches only.
  /^(?:Error: )?[A-Za-z]{2}$/,
  // A browser denying storage access (blocked by the user's settings or an
  // in-app browser), thrown with no stack from a third-party script.
  /^SecurityError: The request was denied\.?$/,
]

/** Scripts that aren't ours (passed to Sentry.init as denyUrls). */
export const DENY_URLS: RegExp[] = [
  // Scripts the Instagram / Facebook in-app browser injects.
  /^iabjs:\/\//,
  // Browser extensions.
  /^(chrome|moz|safari(-web)?)-extension:\/\//,
]

/**
 * Supabase Auth error codes for a user's own outcome, which the page already
 * explains to them: wrong password, account exists, too many emails, same
 * password, expired or used reset link.
 */
const EXPECTED_AUTH_ERROR_CODES = new Set([
  "invalid_credentials",
  "user_already_exists",
  "over_email_send_rate_limit",
  "same_password",
  "otp_expired",
])

/** The same outcomes by message, for errors without a code. */
const EXPECTED_AUTH_ERROR_MESSAGES = [
  "Invalid login credentials",
  "User already registered",
  "For security purposes, you can only request this after",
  "New password should be different from the old password",
]

// The browsers' messages for a request that never got an answer (offline,
// flaky connection, blocked, the page closed): the whole message, or its end
// after a "<prefix>: " (e.g. "TypeError: Failed to fetch" in a
// PostgrestError). Chrome may add the host in parentheses.
const NETWORK_ERROR_PATTERN = new RegExp(
  "(?:^|: )(?:" +
    [
      "Failed to fetch", // Chrome
      "Load failed", // Safari
      "NetworkError when attempting to fetch resource\\.?", // Firefox
      "Failed to send a request to the Edge Function", // supabase-js
      "Failed to load Stripe\\.js", // Stripe's script didn't load (blocked)
      "Stripe\\.js not available",
      // supabase-js's auth lock taken over by another tab or request.
      "AbortError: Lock was stolen by another request",
      "AbortError: Lock broken by another request with the 'steal' option\\.?",
    ].join("|") +
    ")(?: \\([^)]*\\))?$",
)

type ErrorFields = { name?: unknown; message?: unknown; code?: unknown }

function fields(error: unknown): ErrorFields {
  return error && typeof error === "object" ? (error as ErrorFields) : {}
}

const messageOf = (error: unknown) => {
  const { message } = fields(error)

  return typeof message === "string" ? message : ""
}

/** Whether the error is a user's own outcome that the page already shows. */
export function isExpectedAuthError(error: unknown): boolean {
  const { code, name } = fields(error)

  if (name === "AuthSessionMissingError") return true // expired / used link
  if (typeof code === "string" && EXPECTED_AUTH_ERROR_CODES.has(code)) {
    return true
  }

  const message = messageOf(error)

  return (
    typeof name === "string" &&
    name.startsWith("Auth") &&
    EXPECTED_AUTH_ERROR_MESSAGES.some((expected) => message.includes(expected))
  )
}

/**
 * Whether the error is a request that never got an answer. Also a
 * PostgrestError (a plain object) whose message is the fetch error.
 */
export function isNetworkError(error: unknown): boolean {
  return NETWORK_ERROR_PATTERN.test(messageOf(error))
}

export type SentryFilterContext = {
  /** The Vite mode: "production", "staging", … */
  mode: string
  hostname: string
  /** navigator.webdriver: the browser is driven by a test (Maestro, …). */
  isAutomated: boolean
}

/**
 * Whether the event comes from tests on staging: an automated browser or a
 * local build. Never on production.
 */
export function isTestTraffic({
  hostname,
  isAutomated,
  mode,
}: SentryFilterContext): boolean {
  if (mode === "production") return false

  return isAutomated || hostname === "localhost" || hostname === "127.0.0.1"
}

/**
 * Sentry's beforeSend: drops test traffic and the user outcomes the pages
 * already explain; sends network failures as warnings (the page shows its
 * error and retry, but an outage stays visible). Everything else is sent as
 * it is.
 */
export function filterEvent(
  event: ErrorEvent,
  hint: EventHint,
  context: SentryFilterContext,
): ErrorEvent | null {
  if (isTestTraffic(context)) return null

  const error = hint.originalException

  if (isExpectedAuthError(error)) return null

  if (isNetworkError(error)) return { ...event, level: "warning" }

  return event
}

export function beforeSend(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  return filterEvent(event, hint, {
    hostname: window.location.hostname,
    isAutomated: navigator.webdriver === true,
    mode: import.meta.env.MODE,
  })
}
